import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { PageContext, UserContext, ThemeContext } from '../Context';
import { 
  Typography, 
  List, 
  ListItem, 
  ListItemText, 
  IconButton, 
  Alert,
  Button,
  Box,
  Tooltip,
  Snackbar
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { logAction, LOG_ACTIONS, contentLengthLog } from '../../utils/logClient';

function MyInsertions() {
  const { user } = useContext(UserContext);
  const { theme } = useContext(ThemeContext);
  const [userInsertions, setUserInsertions] = useState([]);
  const [copiedInsertion, setCopiedInsertion] = useState(null);
  const [error, setError] = useState('');
  const { page, setPage } = useContext(PageContext);
  const [copyNotification, setCopyNotification] = useState(false);
  const [copiedText, setCopiedText] = useState('');
  
  // Déterminer si le thème est sombre
  const isDark = theme === 'dark';

  // Fetch user's insertions
  useEffect(() => {
    const fetchInsertions = async () => {
      try {
        const token = sessionStorage.getItem('authToken');
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        };

        const response = await axios.get(`/api/user/${user}/insertions`, config);
        setUserInsertions(response.data.myInsertions || []);
      } catch (err) {
        setError('Erreur lors de la récupération des insertions');
        console.error(err.response ? err.response.data : err);
      }
    };

    if (user) {
      fetchInsertions();
    }
  }, [user]);

  // Fermer la notification
  const handleCloseNotification = () => {
    setCopyNotification(false);
  };

  // Afficher la notification
  const showCopyNotification = (text) => {
    setCopiedText(text);
    setCopyNotification(true);
  };

  // Fonction pour extraire le texte brut du HTML pour l'affichage
  const stripHtmlTags = (html) => {
    if (!html || typeof html !== 'string') return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // Handle copying insertion to clipboard (with HTML formatting)
  const handleCopyInsertion = async (insertion) => {
    try {
      // Utiliser l'API Clipboard moderne pour copier à la fois le texte et le HTML
      const htmlContent = insertion.content || '';
      const textContent = stripHtmlTags(htmlContent);
      
      // Créer un ClipboardItem avec les deux formats
      const type = 'text/html';
      const blob = new Blob([htmlContent], { type });
      const textBlob = new Blob([textContent], { type: 'text/plain' });
      
      const data = [
        new ClipboardItem({
          'text/html': blob,
          'text/plain': textBlob
        })
      ];
      
      await navigator.clipboard.write(data);
      
      // Log de la copie d'insertion
      logAction(LOG_ACTIONS.INSERTION_COPY, null, {
        insertionName: insertion.name,
        ...contentLengthLog(insertion.content)
      });
      
      setCopiedInsertion(insertion.name);
      showCopyNotification(insertion.name);
      setTimeout(() => setCopiedInsertion(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback vers la méthode simple si l'API moderne ne fonctionne pas
      try {
        const textContent = stripHtmlTags(insertion.content);
        await navigator.clipboard.writeText(textContent);
        
        // Log de la copie d'insertion (fallback)
        logAction(LOG_ACTIONS.INSERTION_COPY, null, {
          insertionName: insertion.name,
          ...contentLengthLog(insertion.content)
        });
        
        setCopiedInsertion(insertion.name);
        showCopyNotification(insertion.name);
        setTimeout(() => setCopiedInsertion(null), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
        setError('Échec de la copie dans le presse-papiers');
      }
    }
  };

  return (
    <div style={{ 
      maxWidth: 550, 
      margin: '0 auto',
              backgroundColor: isDark ? '#1a252f' : 'inherit',
      minHeight: '100%'
    }}>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 1.5,
            backgroundColor: isDark ? 'rgba(244, 67, 54, 0.15)' : undefined,
            color: isDark ? '#ff6b6b' : undefined,
            '& .MuiAlert-icon': {
              color: isDark ? '#ff6b6b' : undefined
            }
          }}
        >
          {error}
        </Alert>
      )}

      <List dense sx={{ 
        pt: 0.5,
      }}>
        {userInsertions
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((insertion) => (
            <ListItem 
              key={insertion.name}
              sx={{
                py: 0.5,
                borderBottom: '1px solid',
                borderColor: isDark ? '#2d3f4c' : 'divider',
                minHeight: 32,
                backgroundColor: isDark ? '#22303e' : 'inherit',
                '&:hover': {
                  bgcolor: isDark ? 'rgba(79, 195, 247, 0.1)' : 'action.hover',
                }
              }}
              secondaryAction={
                <Tooltip title="Copier" placement="left">
                  <IconButton 
                    edge="end" 
                    onClick={() => handleCopyInsertion(insertion)}
                    color={copiedInsertion === insertion.name ? 'success' : '1976d2'}
                    size="small"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemText 
                primary={insertion.name}
                secondary={copiedInsertion === insertion.name ? 'Copié !' : ''}
                primaryTypographyProps={{ 
                  fontSize: 15,
                  color: isDark ? '#ecf0f1' : 'inherit'
                }}
                secondaryTypographyProps={{ 
                  fontSize: 13,
                  color: isDark ? '#4fc3f7' : 'inherit'
                }}
              />
            </ListItem>
          ))}
      </List>

      {userInsertions.length === 0 && (
        <Typography 
          variant="body2" 
          sx={{ 
            textAlign: 'center', 
            mt: 1.5,
            color: isDark ? '#95a5a6' : 'text.secondary'
          }}
        >
          Aucune insertion enregistrée
        </Typography>
      )}
      <Box sx={{ 
        display: 'flex',
        justifyContent: 'center',
        mt: 2,
        mb: 1
      }}>
        <Button
          variant="contained"
          startIcon={<EditNoteIcon />}
          sx={{
            borderRadius: 28,
            px: 1.5,
            py: 0.7,
            boxShadow: 3,
            textTransform: 'none',
            backgroundColor: isDark ? '#4fc3f7' : 'primary.main',
            color: isDark ? '#000' : 'white',
            '&:hover': {
              backgroundColor: isDark ? '#29b6f6' : 'primary.dark',
            }
          }}
          onClick={() => { setPage('Insertions'); }}
        >
          Modifier
        </Button>
      </Box>

      {/* Notification de copie */}
      <Snackbar
        open={copyNotification}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity="success" 
          sx={{ 
            width: '100%',
            backgroundColor: isDark ? 'rgba(76, 175, 80, 0.15)' : undefined,
            color: isDark ? '#4ecdc4' : undefined,
            '& .MuiAlert-icon': {
              color: isDark ? '#4ecdc4' : undefined
            }
          }}
        >
          {`"${copiedText}" copié dans le presse-papier`}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default MyInsertions;



